<template>
  <main class="container is-fluid">
    <CreateButton class="is-pulled-right" />
    <h1 class="title">Secrets</h1>

    <div
      class="notification is-danger"
      v-if="error"
    >
      {{ error }}
    </div>

    <Paginator
      :response="secretPage ?? undefined"
      v-slot="{ results }"
      :loading="loading"
      allow-empty
      v-model:page="page"
      singular="secret"
      plural="secrets"
    >
      <table
        class="table is-fullwidth"
        :class="{ 'is-hoverable': results.length }"
      >
        <thead>
          <tr>
            <th class="is-narrow">ID</th>
            <th>Name</th>
            <th
              class="is-narrow"
              v-if="selectable"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!results.length">
            <td :colspan="selectable ? 3 : 2">
              <div class="notification is-warning">No secret to display.</div>
            </td>
          </tr>
          <Row
            v-for="secret in results"
            :key="secret.id"
            :secret="secret"
            :selectable="selectable"
            v-on:select="() => $emit('select', secret)"
          />
        </tbody>
      </table>
    </Paginator>
  </main>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Paginator from '@/components/Paginator.vue'
import CreateButton from '@/components/Secret/CreateButton.vue'
import Row from '@/components/Secret/Row.vue'
import { errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useSecretStore } from '@/stores'
import type { SecretLight } from '@/types/secret'

export default defineComponent({
  mixins: [truncateMixin],
  components: {
    CreateButton,
    Paginator,
    Row,
  },
  emits: {
    select: (value: SecretLight) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  props: {
    /**
     * Whether buttons should be shown to allow selecting a secret. This will cause a `select` event to be emitted.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    page: 1,
    loading: false,
    error: null as string | null,
  }),
  computed: {
    ...mapState(useSecretStore, ['secretPage']),
  },
  methods: {
    ...mapActions(useSecretStore, ['list']),
  },
  watch: {
    page: {
      immediate: true,
      async handler(newValue) {
        this.loading = true
        try {
          await this.list({ page: newValue })
        } catch (err) {
          this.error = errorParser(err)
        } finally {
          this.loading = false
        }
      },
    },
  },
})
</script>
